import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Form, Input } from "antd";
import Add from "../../assets/Add";
import Edit from "../../assets/Edit";
import Delete from "../../assets/Delete";
import columns from "../../columns/pipeline/column";
import SkeletonTable from "../../components/table/SkeletonTable";
import LoadableButton from "../../components/button/LoadableButton";
import ModalComponent from "../../components/modals/ModalComponent";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import {
  CreatePipelineApi,
  DeletePipelineApi,
  EditPipelineApi,
  getAllPipelineApi,
} from "../../api/request/pipeline";
import GlobalTooltip from "../../components/common/GlobalTooltip";
import ServiceView from "../../assets/ServiceView";

const Pipeline = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [pipelineId, setPipelineId] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [mode, setMode] = useState("create");

  const renderActionButtons = (item) => (
    <div className="flex items-center gap-3">
      <GlobalTooltip title={"Edit Service Flow"}>
        <button
          onClick={(e) => {
            e?.stopPropagation();
            setPipelineId(item?.id);
            form.setFieldsValue({ name: item?.name });
            setMode("edit");
            setIsOpen(true);
          }}
        >
          <Edit />
        </button>
      </GlobalTooltip>
      <GlobalTooltip title={"View Service Flow"}>
        <button
          onClick={(e) => {
            e?.stopPropagation();
            const safePipeline = {
              id: item.id,
              admin_id: item.admin_id,
              name: item.name ?? "",
              status: item.status,
              created_at: item.created_at,
              updated_at: item.updated_at,
              pipeline_manage_tasks: item.pipeline_manage_tasks,
            };
            navigate(`/service_flow/${item?.id}`, {
                 state: { pipeline: safePipeline },
            });
          }}
        >
          <ServiceView />
        </button>
      </GlobalTooltip>
      <GlobalTooltip title={"Delete Service Flow"}>
        <button
          onClick={(e) => {
            e?.stopPropagation();
            setPipelineId(item?.id);
            setIsDeleteModalOpen(true);
          }}
        >
          <Delete />
        </button>
      </GlobalTooltip>
    </div>
  );
  const getPipelineList = async () => {
    try {
      setIsLoading(true);
      const { data } = await getAllPipelineApi();
      if (data.status) {
        setData(
          data?.data?.map((item) => {
            return {
              ...item,
              name: <p className="capitalize">{item?.name}</p>,
              action: renderActionButtons(item),
            };
          })
        );
        setTotal(data?.total_record);
        setIsLoading(false);
      }
    } catch (err) {
      setIsLoading(false);
    }
  };

  const editPipeline = async (values) => {
    setIsUpdateLoading(true);
    try {
      const params = {
        pipeline_temp_id: pipelineId,
        name: values.name,
      };
      const { data } = await EditPipelineApi(params);
      if (data.status) {
        toast.success(data?.message);
        setIsOpen(false);
        setData((prevData) =>
          prevData?.map((item) =>
            item?.id === pipelineId ? { ...item, name: values?.name } : item
          )
        );
        getPipelineList();
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const createPipeline = async (value) => {
    try {
      setIsCreateLoading(true);
      const { data } = await CreatePipelineApi(value);
      if (data?.status) {
        toast.success(data?.message);
        setIsOpen(false);
        const newItem = {
          ...data?.data,
          action: renderActionButtons(data?.data),
        };
        setData((prev) => [...prev, newItem]);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsCreateLoading(false);
    }
  };

  const deletePipeline = async () => {
    try {
      setIsDeleteLoading(true);
      const { data } = await DeletePipelineApi({ id: pipelineId });
      if (data.status) {
        toast?.success(data?.message);
        setData((prevData) =>
          prevData?.filter((item) => item.id !== pipelineId)
        );
        setIsDeleteModalOpen(false);
        setPipelineId("");
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const onRowClick = (data) => {
    const safePipeline = {
      id: data.id,
      admin_id: data.admin_id,
      name: data.name?.props?.children ?? "",
      status: data.status,
      created_at: data.created_at,
      updated_at: data.updated_at,
      pipeline_manage_tasks: data.pipeline_manage_tasks,
    };

    navigate(`/service_flow/${data?.id}`, {
      state: { pipeline: safePipeline },
    });
  };

  useEffect(() => {
    getPipelineList();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setMode("create");
      setPipelineId("");
    }
  }, [isOpen]);

  return (
    <>
      <div className="border rounded-lg bg-White pb-4">
        <div className="py-5 px-4 flex items-center justify-between">
          <h2 className="title">Services Flow {total ? `(${total})` : null}</h2>
          <button
            className="btn-primary !px-4 flex items-center gap-2"
            onClick={() => setIsOpen(true)}
          >
            <Add />
            <span>Add Service Flow</span>
          </button>
        </div>

        <SkeletonTable
          columns={columns}
          searchingFields={["name"]}
          data={data}
          isLoading={isLoading}
          onRowClick={(data) => onRowClick(data)}
        />
      </div>

      <ModalComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={mode === "create" ? "Create Service Flow" : "Edit Service Flow"}
      >
        <Form
          autoComplete="off"
          layout="vertical"
          form={form}
          className="w-full"
          onFinish={(values) => {
            if (mode === "create") {
              createPipeline(values);
            } else {
              editPipeline(values);
            }
          }}
        >
          <div className="input-white">
            <Form.Item
              name="name"
              label="Service Flow Name"
              className="mb-0"
              rules={[
                { required: true, message: "Please enter service flow name!" },
              ]}
            >
              <Input
                placeholder="Service Flow name"
                size="large"
                className="capitalize"
              />
            </Form.Item>
          </div>

          <div className="text-center mt-5">
            <LoadableButton
              type="submit"
              lable={mode === "create" ? "Submit" : "Update"}
              isLoading={mode === "create" ? isCreateLoading : isUpdateLoading}
              loadingLable={
                mode === "create" ? "Creating Flow..." : "Updating Flow..."
              }
            />
          </div>
        </Form>
      </ModalComponent>

      {/* --- Delete confirmation modal --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this service flow?`}
        onConfirm={deletePipeline}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting Flow...`}
      />
    </>
  );
};

export default Pipeline;
